/**
 * @typedef {Object} GAItem
 * @property {string} item_id
 * @property {string} item_name
 * @property {number} price
 * @property {number} [quantity]
 */

export const ECOMMERCE_EVENT = /** @type {const} */ ({
    VIEW_CART: 'view_cart',
    ADD_TO_CART: 'add_to_cart',
    REMOVE_FROM_CART: 'remove_from_cart',
    ADD_PAYMENT_INFO: 'add_payment_info',
    ADD_SHIPPING_INFO: 'add_shipping_info',
    ADD_TO_WISHLIST: 'add_to_wishlist',
    BEGIN_CHECKOUT: 'begin_checkout',
    PURCHASE: 'purchase',
    REFUND: 'refund',
    SELECT_ITEM: 'select_item',
    SELECT_PROMOTION: 'select_promotion',
    VIEW_ITEM: 'view_item',
    VIEW_ITEM_LIST: 'view_item_list',
    VIEW_PROMOTION: 'view_promotion'
});

const GA_CURRENCY = 'USD';

/**
 * @type {Array<{ eventName: typeof ECOMMERCE_EVENT[keyof typeof ECOMMERCE_EVENT], params: Object }>}
 */
const EVENT_QUEUE = [];

/**
 * @typedef {typeof ECOMMERCE_EVENT[keyof typeof ECOMMERCE_EVENT]} GAEventName
 */
/**
 * The eventName should be one of the EC_EVENT values
 * @param {GAEventName} eventName
 * @param {Object} ecommerceData
 * @returns
 */
function pushEvent(eventName, ecommerceData = {}, logging = true) {
    if (!window.gtag) {
        EVENT_QUEUE.push({ eventName, ecommerceData });
        return;
    }
    logging && console.log('GA Event Push:', { eventName, ...ecommerceData });
    window.gtag('event', eventName, ecommerceData);
}

function flushQueuedEvents(logging = true) {
    if (!window.gtag) {
        return;
    }

    while (EVENT_QUEUE.length) {
        const { eventName, ecommerceData } = EVENT_QUEUE.shift();
        logging && console.log('Flushed GA Event:', { eventName, ...ecommerceData });
        window.gtag('event', eventName, ecommerceData);
    }
}

const EcommerceTracker = {
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    viewCart: ({ items = [], value = 1, currency = GA_CURRENCY }) =>
        pushEvent(ECOMMERCE_EVENT.VIEW_CART, {
            currency,
            value,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    removeFromCart: ({ items = [], value = 1, currency = GA_CURRENCY }) =>
        pushEvent(ECOMMERCE_EVENT.REMOVE_FROM_CART, {
            currency,
            value,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    addToCart: ({ items = [], value = 1, currency = GA_CURRENCY }) =>
        pushEvent(ECOMMERCE_EVENT.ADD_TO_CART, {
            currency,
            value,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    viewItem: ({ items = [], value = 1, currency = GA_CURRENCY }) =>
        pushEvent(ECOMMERCE_EVENT.VIEW_ITEM, {
            currency,
            value,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {string} [params.item_list_id]
     * @param {string} [params.item_list_name]
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    viewItemList: ({
        items = [],
        value = 1,
        currency = GA_CURRENCY,
        item_list_id,
        item_list_name
    }) =>
        pushEvent(ECOMMERCE_EVENT.VIEW_ITEM_LIST, {
            item_list_id,
            item_list_name,
            currency,
            value,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {string} [params.item_list_id]
     * @param {string} [params.item_list_name]
     */
    selectItem: ({ items = [], item_list_id, item_list_name }) =>
        pushEvent(ECOMMERCE_EVENT.SELECT_ITEM, {
            item_list_id,
            item_list_name,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    addToWishlist: ({ items = [], value = 1, currency = GA_CURRENCY }) =>
        pushEvent(ECOMMERCE_EVENT.ADD_TO_WISHLIST, {
            currency,
            value,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {string} [params.coupon]
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    beginCheckout: ({ items = [], value = 1, coupon, currency = GA_CURRENCY }) =>
        pushEvent(ECOMMERCE_EVENT.BEGIN_CHECKOUT, {
            currency,
            value,
            coupon,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {string} [params.coupon]
     * @param {string} [params.shipping_tier]
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    addShippingInfo: ({ items = [], value = 1, currency = GA_CURRENCY, coupon, shipping_tier }) =>
        pushEvent(ECOMMERCE_EVENT.ADD_SHIPPING_INFO, {
            currency,
            value,
            shipping_tier,
            coupon,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {string} [params.coupon]
     * @param {string} [params.payment_type]
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     */
    addPaymentInfo: ({ items = [], value = 1, currency = GA_CURRENCY, payment_type, coupon }) =>
        pushEvent(ECOMMERCE_EVENT.ADD_PAYMENT_INFO, {
            currency,
            value,
            payment_type,
            coupon,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {GAItem[]} params.items
     * @param {string} params.transaction_id
     * @param {string} [params.coupon]
     * @param {string} [params.payment_type]
     * @param {number} [params.value=1]
     * @param {string} [params.currency=GA_CURRENCY]
     * @param {number} [params.tax=0]
     * @param {number} [params.shipping=0]
     * @param {string} [params.customer_type]
     */
    purchase: ({
        items = [],
        value,
        currency = GA_CURRENCY,
        transaction_id,
        tax = 0,
        shipping = 0,
        coupon,
        customer_type
    }) =>
        pushEvent(ECOMMERCE_EVENT.PURCHASE, {
            currency,
            transaction_id,
            customer_type,
            value,
            tax,
            shipping,
            coupon,
            items
        }),
    /**
     *
     * @param {Object} params
     * @param {string} params.transaction_id
     * @param {number} [params.value=0]
     * @param {string} [params.currency=GA_CURRENCY]
     * @param {GAItem[]} [params.items]
     */
    refund: ({ transaction_id, value = 0, currency = GA_CURRENCY, items = [] }) =>
        pushEvent(ECOMMERCE_EVENT.REFUND, {
            currency,
            transaction_id,
            value,
            items
        })
};

export { EcommerceTracker, flushQueuedEvents };
